import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Pin, PinOff, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api, type NoteView } from '../../lib/client.ts';
import { Badge } from '../ui/badge.tsx';
import { Button } from '../ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.tsx';
import { isBlankHtml } from '../ui/rich-text.tsx';
import {
  DropsFormatting,
  NoteBody,
  editingDropsFormatting,
  noteHtmlForEditing,
} from './note-body.tsx';
import { NoteEditor } from './note-editor.tsx';

/**
 * A note opened full size (TS-1873): the whole body in a centred dialog instead of a card in
 * the sidebar, with the same actions the card has. Editing the text is for its author who can
 * also write to the record (`canEdit`: the API checks write on the record before authorship),
 * deleting is the author's (`canDelete`), pinning is anyone's who can write to the record
 * (`canPin`) — the API's rules, mirrored here only to hide what would be refused. Closing with
 * unsaved edits asks first. It fetches nothing: the note comes from the list that opened it,
 * and every write refreshes the `['notes', …]` queries so the card behind it follows.
 */
export function NoteDetailDialog({
  note,
  canEdit,
  canDelete,
  canPin,
  sourceLabel,
  onClose,
}: {
  note: NoteView;
  canEdit: boolean;
  canDelete: boolean;
  canPin: boolean;
  /** Where the note is ("Job: …"), when it is on a record related to the one listing it. */
  sourceLabel?: string | undefined;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [initial, setInitial] = useState('');
  const [body, setBody] = useState('');
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState<'delete' | 'discard' | null>(null);
  const dirty = editing && body !== initial;
  const startEditing = () => {
    const start = noteHtmlForEditing(note.body, note.bodyFormat);
    setInitial(start);
    setBody(start);
    setEditing(true);
  };

  const refresh = () => qc.invalidateQueries({ queryKey: ['notes'] });
  const save = useMutation({
    mutationFn: () => api.notes.patch(note.id, { body, bodyFormat: 'html' }),
    onSuccess: async () => {
      await refresh();
      setEditing(false);
    },
  });
  const pin = useMutation({
    mutationFn: () => api.notes.patch(note.id, { pinned: !note.pinned }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: () => api.notes.remove(note.id),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  const error = save.error ?? pin.error ?? remove.error;

  const requestClose = () => {
    if (dirty) setConfirming('discard');
    else onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && requestClose()}>
      <DialogContent
        className="max-w-2xl"
        data-testid="note-detail"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {note.pinned && <Pin className="text-brand size-4" aria-label="Pinned" />}
            Note by{' '}
            {note.author?.name ??
              (note.createdByAutomation
                ? note.createdByAutomation.name
                  ? `automation "${note.createdByAutomation.name}"`
                  : 'a deleted automation'
                : 'a former member')}
            {sourceLabel && <Badge variant="secondary">{sourceLabel}</Badge>}
          </DialogTitle>
          <DialogDescription>
            <time dateTime={note.createdAt}>{new Date(note.createdAt).toLocaleString()}</time>
            {note.editedAt && ' · edited'}
          </DialogDescription>
        </DialogHeader>

        {editing ? (
          <NoteEditor
            autoFocus
            target={{ entityType: note.entityType, entityId: note.entityId }}
            label="Note"
            value={initial}
            onChange={setBody}
            className="max-h-[60vh] min-h-40"
          />
        ) : (
          <div className="max-h-[60vh] overflow-y-auto" data-testid="note-detail-body">
            <NoteBody body={note.body} format={note.bodyFormat} />
          </div>
        )}
        {editing && editingDropsFormatting(initial, note.bodyFormat) && <DropsFormatting />}

        {error && (
          <p className="text-danger text-sm" role="alert">
            {describeError(error)}
          </p>
        )}

        {confirming === 'delete' ? (
          <DialogFooter className="items-center" data-testid="confirm-delete">
            <span className="mr-auto text-sm">Delete this note?</span>
            <Button variant="ghost" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              data-testid="confirm-delete-note"
            >
              Delete
            </Button>
          </DialogFooter>
        ) : confirming === 'discard' ? (
          <DialogFooter className="items-center" data-testid="confirm-discard">
            <span className="mr-auto text-sm">Discard your changes?</span>
            <Button variant="ghost" onClick={() => setConfirming(null)}>
              Keep editing
            </Button>
            <Button variant="destructive" onClick={onClose} data-testid="discard-changes">
              Discard
            </Button>
          </DialogFooter>
        ) : (
          <DialogFooter>
            {canDelete && (
              <Button
                variant="ghost"
                className="mr-auto"
                onClick={() => setConfirming('delete')}
                data-testid="delete-note"
              >
                <Trash2 /> Delete
              </Button>
            )}
            {canPin && (
              <Button
                variant="ghost"
                onClick={() => pin.mutate()}
                disabled={pin.isPending}
                aria-label={note.pinned ? 'Unpin note' : 'Pin note'}
              >
                {note.pinned ? <PinOff /> : <Pin />} {note.pinned ? 'Unpin' : 'Pin'}
              </Button>
            )}
            {editing ? (
              <>
                <Button variant="ghost" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => save.mutate()}
                  disabled={!dirty || isBlankHtml(body) || save.isPending}
                  data-testid="save-note"
                >
                  Save
                </Button>
              </>
            ) : (
              <>
                {canEdit && (
                  <Button variant="ghost" onClick={startEditing} data-testid="edit-note">
                    <Pencil /> Edit
                  </Button>
                )}
                <Button variant="ghost" onClick={onClose} data-testid="close-note">
                  Close
                </Button>
              </>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
